import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Plus, Trash2, Loader2 } from "lucide-react";
import { UNIT_OPTIONS, normUnit } from "./adminShared";

/* ============ INVENTORY PANEL ============ */
export default function InventoryPanel({ products }) {
  const [form, setForm] = useState({ inventory_items: [], incoming_shipments: [] });
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [inventoryOverview, setInventoryOverview] = useState(null);
  const [usageDays, setUsageDays] = useState(30);
  const [usageDaily, setUsageDaily] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const makeClientId = (prefix) => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  useEffect(() => {
    let cancelled = false;
    const loadInventoryConfig = async () => {
      try {
        const res = await api.get("/admin/inventory/config");
        if (cancelled) return;
        setForm({
          inventory_items: res.data.inventory_items || [],
          incoming_shipments: res.data.incoming_shipments || [],
        });
      } catch (e) {
        if (!cancelled) {
          toast.error("Kunde inte ladda lagerdata");
        }
      }
    };

    const loadUsage = async () => {
      try {
        const res = await api.get(`/admin/inventory/usage-daily?days=${usageDays}`);
        if (cancelled) return;
        setUsageDaily(res.data);
      } catch (e) {
        if (!cancelled) {
          toast.error("Kunde inte ladda förbrukningsdata");
        }
      }
    };

    loadInventoryConfig();
    loadUsage();
    return () => { cancelled = true; };
  }, [usageDays]);

  const loadUsageDaily = async () => {
    setLoadingUsage(true);
    try {
      const res = await api.get(`/admin/inventory/usage-daily?days=${usageDays}`);
      setUsageDaily(res.data);
    } catch (e) {
      if (e.message !== "Network Error") {
        toast.error(e.response?.data?.detail || "Kunde inte ladda förbrukningsdata");
      }
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/inventory/config", {
        inventory_items: form.inventory_items || [],
        incoming_shipments: form.incoming_shipments || [],
      });
      toast.success("Lager sparat");
      // Silently update graph data in background to prevent popup interruptions
      loadUsageDaily().catch(() => {});
    } catch (e) {
      console.error("Save error:", e);
      if (e.message !== "Network Error" && e.code !== "ERR_NETWORK") {
        const detail = e.response?.data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map(d => d?.msg || JSON.stringify(d)).join(", ")
          : (typeof detail === "string" ? detail : e.message);
        toast.error(msg || "Kunde inte spara lager");
      } else {
        // Fallback for timeout/network error if it actually did save.
        toast.success("Lager sparat (via bakgrund)");
      }
    } finally {
      setSaving(false);
    }
  };

  const addInventoryItem = () => {
    setForm(f => ({
      ...f,
      inventory_items: [
        ...(f.inventory_items || []),
        {
          id: makeClientId("inv"),
          name: "",
          unit: "st",
          current_stock: 0,
          reorder_point: 0
        }
      ]
    }));
  };

  const updateInventoryItem = (index, field, value) => {
    setForm(f => ({
      ...f,
      inventory_items: (f.inventory_items || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeInventoryItem = (index) => {
    setForm(f => ({
      ...f,
      inventory_items: (f.inventory_items || []).filter((_, i) => i !== index)
    }));
  };

  const addIncomingShipment = () => {
    setForm(f => ({
      ...f,
      incoming_shipments: [
        ...(f.incoming_shipments || []),
        {
          id: makeClientId("ship"),
          item_id: (f.inventory_items || [])[0]?.id || "",
          item_name: "",
          quantity: 0,
          expected_at: new Date().toISOString().slice(0, 10),
          status: "ordered",
        }
      ]
    }));
  };

  const updateIncomingShipment = (index, field, value) => {
    setForm(f => ({
      ...f,
      incoming_shipments: (f.incoming_shipments || []).map((shipment, i) =>
        i === index ? { ...shipment, [field]: value } : shipment
      )
    }));
  };

  const removeIncomingShipment = (index) => {
    setForm(f => ({
      ...f,
      incoming_shipments: (f.incoming_shipments || []).filter((_, i) => i !== index)
    }));
  };

  const calculateInventory = async () => {
    setCalculating(true);
    try {
      const res = await api.get("/admin/inventory/overview");
      setInventoryOverview(res.data);
      loadUsageDaily().catch(() => {});
      toast.success("Lagerberäkning klar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte beräkna lager");
    } finally {
      setCalculating(false);
    }
  };

  // Which products consume each inventory item (from their recipes).
  const usedBy = {};
  (products || []).forEach(p => (p.recipe || []).forEach(r => {
    if (r.item_id) (usedBy[r.item_id] = usedBy[r.item_id] || []).push(p.name);
  }));
  const lowStock = (form.inventory_items || []).filter(
    i => Number(i.reorder_point) > 0 && Number(i.current_stock) <= Number(i.reorder_point)
  );

  return (
    <div className="max-w-5xl" data-testid="inventory-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Lager</h2>
          <p className="text-[#E8E8EA] text-xs mt-1">Råvaror dras automatiskt när en order accepteras. Recept anges per produkt under Meny.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={calculateInventory} disabled={calculating} className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold">
            {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Beräkna behov"}
          </Button>
          <select
            value={usageDays}
            onChange={e => setUsageDays(parseInt(e.target.value, 10))}
            className="bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-xs px-2"
          >
            <option value={7}>7 dagar</option>
            <option value={14}>14 dagar</option>
            <option value={30}>30 dagar</option>
            <option value={60}>60 dagar</option>
          </select>
          <Button type="button" onClick={handleSave} disabled={saving} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Spara Lager"}
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {lowStock.length > 0 && (
          <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4">
            <p className="text-[#FCA5A5] font-semibold text-sm mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" /> {lowStock.length} vara{lowStock.length > 1 ? "r" : ""} under min-nivå – dags att beställa
            </p>
            <div className="flex flex-wrap gap-2">
              {lowStock.map(i => (
                <span key={i.id} className="text-xs bg-[#EF4444]/15 text-[#FCA5A5] px-2.5 py-1 rounded-full">
                  {i.name}: {i.current_stock} {i.unit}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
              <Package className="w-4 h-4 text-[#D4B483]" /> Lagervaror
            </h3>
            <Button type="button" onClick={addInventoryItem} variant="outline" className="h-8 text-xs border-[#27272A]">
              <Plus className="w-3 h-3 mr-1" /> Lägg till vara
            </Button>
          </div>

          {(form.inventory_items || []).length === 0 && (
            <div className="text-xs text-[#B4B4BC] p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
              Inga lagervaror ännu. Lägg till minst en vara för att aktivera lagerberäkning.
            </div>
          )}

          <div className="space-y-3">
            {(form.inventory_items || []).length > 0 && (
              <div className="hidden sm:grid sm:grid-cols-5 gap-2 px-1">
                <p className="text-xs text-[#B4B4BC]">Varunamn</p>
                <p className="text-xs text-[#B4B4BC]">Enhet</p>
                <p className="text-xs text-[#B4B4BC]">Nuvarande lager</p>
                <p className="text-xs text-[#B4B4BC]">Min nivå (larm)</p>
                <p className="text-xs text-[#B4B4BC]">Åtgärd</p>
              </div>
            )}
            {(form.inventory_items || []).map((item, idx) => (
              <div key={item.id || idx} className="p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A] space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <Input value={item.name || ""} onChange={e => updateInventoryItem(idx, "name", e.target.value)} placeholder="T.ex. Högrevsfärs" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-xs" />
                  <select
                    value={normUnit(item.unit)}
                    onChange={e => updateInventoryItem(idx, "unit", e.target.value)}
                    className="bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-xs px-2"
                  >
                    {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                  <Input type="number" value={item.current_stock ?? 0} onChange={e => updateInventoryItem(idx, "current_stock", parseFloat(e.target.value) || 0)} placeholder="T.ex. 25" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-xs" />
                  <Input type="number" value={item.reorder_point ?? 0} onChange={e => updateInventoryItem(idx, "reorder_point", parseFloat(e.target.value) || 0)} placeholder="T.ex. 10" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-xs" />
                  <Button type="button" variant="outline" onClick={() => removeInventoryItem(idx)} className="h-9 text-xs border-[#3F3F46] text-[#EF4444]"><Trash2 className="w-3 h-3 mr-1" /> Ta bort</Button>
                </div>
                {usedBy[item.id]?.length > 0 && (
                  <p className="text-[11px] text-[#A8ABB2]">Används av: {usedBy[item.id].join(", ")}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#EDEDED]">Inkommande leveranser</h3>
            <Button type="button" onClick={addIncomingShipment} variant="outline" className="h-8 text-xs border-[#27272A]"><Plus className="w-3 h-3 mr-1" /> Inkommande</Button>
          </div>
          {(form.incoming_shipments || []).map((shipment, idx) => (
            <div key={shipment.id || idx} className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-2 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
              <select value={shipment.item_id || ""} onChange={e => updateIncomingShipment(idx, "item_id", e.target.value)} className="bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-xs px-2">
                <option value="">Välj vara</option>
                {(form.inventory_items || []).map(i => (<option key={i.id} value={i.id}>{i.name || i.id}</option>))}
              </select>
              <Input type="number" step="0.01" value={shipment.quantity ?? 0} onChange={e => updateIncomingShipment(idx, "quantity", parseFloat(e.target.value) || 0)} placeholder="Antal" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-xs" />
              <Input type="date" value={(shipment.expected_at || "").slice(0, 10)} onChange={e => updateIncomingShipment(idx, "expected_at", e.target.value)} className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-xs" />
              <select value={shipment.status || "ordered"} onChange={e => updateIncomingShipment(idx, "status", e.target.value)} className="bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-xs px-2">
                <option value="ordered">Beställd</option>
                <option value="confirmed">Bekräftad</option>
                <option value="in_transit">På väg</option>
                <option value="received">Mottagen</option>
                <option value="cancelled">Avbruten</option>
              </select>
              <Button type="button" variant="outline" onClick={() => removeIncomingShipment(idx)} className="h-9 text-xs border-[#3F3F46] text-[#EF4444]"><Trash2 className="w-3 h-3 mr-1" /> Ta bort</Button>
            </div>
          ))}
        </div>

        {inventoryOverview?.items?.length > 0 && (
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-[#EDEDED]">Beräknad översikt</p>
              {inventoryOverview.low_stock_count > 0 && (
                <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-0 text-[11px]">
                  {inventoryOverview.low_stock_count} under min-nivå
                </Badge>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#B4B4BC] border-b border-[#27272A]">
                    <th className="py-2 pr-3">Vara</th>
                    <th className="py-2 pr-3">Lager</th>
                    <th className="py-2 pr-3">Inkommande</th>
                    <th className="py-2 pr-3">Behov</th>
                    <th className="py-2 pr-3">Snitt / dag</th>
                    <th className="py-2 pr-3">Dagar kvar</th>
                    <th className="py-2 pr-3">Saknas nu</th>
                    <th className="py-2 pr-3">Rek. beställning</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryOverview.items.map(item => (
                    <tr key={item.id} className={`border-b border-[#18181B] text-[#EDEDED] ${item.low_stock ? "bg-[#EF4444]/5" : ""}`}>
                      <td className="py-2 pr-3">{item.name}</td>
                      <td className="py-2 pr-3">{item.current_stock} {item.unit}</td>
                      <td className="py-2 pr-3">{item.incoming_quantity} {item.unit}</td>
                      <td className="py-2 pr-3">{item.required_for_active_orders} {item.unit}</td>
                      <td className="py-2 pr-3">{item.avg_daily_usage} {item.unit}</td>
                      <td className={`py-2 pr-3 ${item.days_remaining !== null && item.days_remaining <= 3 ? "text-[#EF4444] font-semibold" : "text-[#E8E8EA]"}`}>
                        {item.days_remaining === null ? "-" : `${item.days_remaining} d`}
                      </td>
                      <td className={`py-2 pr-3 font-semibold ${item.needed_for_active_orders > 0 ? "text-[#EF4444]" : "text-[#10B981]"}`}>{item.needed_for_active_orders} {item.unit}</td>
                      <td className="py-2 pr-3">{item.recommended_order_qty} {item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[#EDEDED]">Daglig förbrukning</p>
            <Button type="button" onClick={loadUsageDaily} variant="outline" disabled={loadingUsage} className="h-8 text-xs border-[#27272A]">
              {loadingUsage ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Uppdatera
            </Button>
          </div>

          {!usageDaily?.items_summary?.length ? (
            <p className="text-xs text-[#B4B4BC]">Ingen förbrukning registrerad i vald period ännu.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg bg-[#0A0A0A] border border-[#27272A] p-3">
                <p className="text-[11px] text-[#B4B4BC] mb-2">Totalt i perioden</p>
                <div className="space-y-2">
                  {usageDaily.items_summary.slice(0, 8).map(item => (
                    <div key={item.item_id} className="flex items-center justify-between text-xs">
                      <span className="text-[#EDEDED]">{item.name}</span>
                      <span className="text-[#D4B483] font-semibold">{item.total_used} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-[#0A0A0A] border border-[#27272A] p-3">
                <p className="text-[11px] text-[#B4B4BC] mb-2">Snitt per dag</p>
                <div className="space-y-2">
                  {usageDaily.items_summary.slice(0, 8).map(item => (
                    <div key={`${item.item_id}_avg`} className="flex items-center justify-between text-xs">
                      <span className="text-[#EDEDED]">{item.name}</span>
                      <span className="text-[#10B981] font-semibold">{item.avg_daily_used} {item.unit}/dag</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
